// src/components/Documentation.jsx
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar,
  Toolbar,
  Container,
  Typography,
  Box,
  Stack,
  Paper,
  Grid,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CodeIcon from '@mui/icons-material/Code';
import StarIcon from '@mui/icons-material/Star';

const cardSx = {
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 6px 20px rgba(20,30,60,0.05)',
  bgcolor: 'background.paper',
};

const stepNumSx = {
  width: 36,
  height: 36,
  borderRadius: 2,
  display: 'grid',
  placeItems: 'center',
  fontWeight: 800,
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  flexShrink: 0,
};

function Step({ n, icon: Icon, title, children }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 3 }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <Box sx={stepNumSx}>{n}</Box>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            {Icon && <Icon color="primary" fontSize="small" />}
            <Typography variant="h6" fontWeight={800}>
              {title}
            </Typography>
          </Stack>
          <Box sx={{ color: 'text.secondary', '& p': { mt: 0, mb: 1.2 }, '& li': { mb: 0.5 } }}>
            {children}
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
}

// Renders a translated string that may contain a small set of inline tags
// (<b>) - the strings only ever come from our own locale files, never
// user input, so this is safe.
function Html({ text, component = 'p' }) {
  return <Box component={component} dangerouslySetInnerHTML={{ __html: text }} />;
}

function TwoPaths({ left, right }) {
  return (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      <Grid item xs={12} sm={6}>
        <Box sx={{ ...cardSx, p: 2, height: '100%' }}>
          <Chip label={left.label} size="small" color="primary" sx={{ fontWeight: 700, mb: 1 }} />
          <Box component="ol" sx={{ pl: 2.5, m: 0, color: 'text.secondary', fontSize: 14 }}>
            {left.items.map((it, i) => (
              <li key={i} style={{ marginBottom: 4 }}>
                {it}
              </li>
            ))}
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Box sx={{ ...cardSx, p: 2, height: '100%' }}>
          <Chip
            label={right.label}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 700, mb: 1 }}
          />
          <Box component="ol" sx={{ pl: 2.5, m: 0, color: 'text.secondary', fontSize: 14 }}>
            {right.items.map((it, i) => (
              <li key={i} style={{ marginBottom: 4 }}>
                {it}
              </li>
            ))}
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}

function Callout({ text, tone = 'info' }) {
  const bg = tone === 'star' ? '#fff8e6' : '#eef3ff';
  const border = tone === 'star' ? '#f0c869' : '#b9caff';
  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: bg,
        border: '1px solid',
        borderColor: border,
        fontSize: 14,
        color: 'text.secondary',
      }}
      dangerouslySetInnerHTML={{ __html: text }}
    />
  );
}

function Faq({ items }) {
  return items.map((item) => (
    <Accordion key={item.q} disableGutters sx={{ ...cardSx, mb: 1, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={700}>{item.q}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography color="text.secondary">{item.a}</Typography>
      </AccordionDetails>
    </Accordion>
  ));
}

export default function Documentation() {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);

  const ownerFaq = t('userGuide.owners.faq', { returnObjects: true });
  const dinerFaq = t('userGuide.diners.faq', { returnObjects: true });
  const plans = t('userGuide.owners.step5.plans', { returnObjects: true });
  const pathAppItems = t('userGuide.owners.step3.pathAppItems', { returnObjects: true });
  const pathNoAppItems = t('userGuide.owners.step3.pathNoAppItems', { returnObjects: true });
  const dinerStep2Items = t('userGuide.diners.step2.items', { returnObjects: true });

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Link
              component={RouterLink}
              to="/showDashboard"
              sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.secondary' }}
            >
              <ArrowBackIcon fontSize="small" />
            </Link>
            <Typography fontWeight={900} color="primary">
              QRProof
            </Typography>
            <Typography color="text.secondary">{t('userGuide.pageTitle')}</Typography>
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
        <Typography variant="h3" sx={{ fontWeight: 900, fontSize: { xs: 30, md: 40 } }} gutterBottom>
          {t('userGuide.heading')}
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 640, mb: 4 }}>
          {t('userGuide.subheading')}
        </Typography>

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 4, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Tab icon={<StorefrontIcon />} iconPosition="start" label={t('userGuide.tabOwners')} />
          <Tab icon={<PhoneIphoneIcon />} iconPosition="start" label={t('userGuide.tabDiners')} />
        </Tabs>

        {/* ============================= OWNERS ============================= */}
        {tab === 0 && (
          <Stack spacing={3}>
            <Step n={1} title={t('userGuide.owners.step1.title')}>
              <Html text={t('userGuide.owners.step1.body')} />
            </Step>

            <Step n={2} title={t('userGuide.owners.step2.title')}>
              <Html text={t('userGuide.owners.step2.body')} />
            </Step>

            <Step n={3} icon={QrCode2Icon} title={t('userGuide.owners.step3.title')}>
              <p>{t('userGuide.owners.step3.intro')}</p>
              <TwoPaths
                left={{ label: t('userGuide.owners.step3.pathAppLabel'), items: pathAppItems }}
                right={{ label: t('userGuide.owners.step3.pathNoAppLabel'), items: pathNoAppItems }}
              />
              <Callout text={t('userGuide.owners.step3.callout')} />
            </Step>

            <Step n={4} icon={LocationOnIcon} title={t('userGuide.owners.step4.title')}>
              <p>{t('userGuide.owners.step4.body')}</p>
            </Step>

            <Step n={5} icon={WorkspacePremiumIcon} title={t('userGuide.owners.step5.title')}>
              <Html text={t('userGuide.owners.step5.body')} />
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{t('userGuide.owners.step5.tablePlan')}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t('userGuide.owners.step5.tableFacilities')}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t('userGuide.owners.step5.tablePrice')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plans.map((p) => (
                    <TableRow key={p.name}>
                      <TableCell sx={{ fontWeight: 700 }}>{p.name}</TableCell>
                      <TableCell>{p.facilities}</TableCell>
                      <TableCell>{p.price}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Step>

            <Step n={6} icon={CodeIcon} title={t('userGuide.owners.step6.title')}>
              <Html text={t('userGuide.owners.step6.body')} />
            </Step>

            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" fontWeight={800} gutterBottom>
                {t('userGuide.owners.faqHeading')}
              </Typography>
              <Faq items={ownerFaq} />
            </Box>
          </Stack>
        )}

        {/* ============================= DINERS ============================= */}
        {tab === 1 && (
          <Stack spacing={3}>
            <Stack direction="row" spacing={0.5} sx={{ color: '#e0ac1a', mb: -1 }}>
              {[...Array(5)].map((_, i) => (
                <StarIcon key={i} fontSize="small" />
              ))}
            </Stack>

            <Step n={1} icon={PhoneIphoneIcon} title={t('userGuide.diners.step1.title')}>
              <Html text={t('userGuide.diners.step1.body')} />
            </Step>

            <Step n={2} icon={QrCode2Icon} title={t('userGuide.diners.step2.title')}>
              <p>{t('userGuide.diners.step2.body')}</p>
              <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                {dinerStep2Items.map((it, i) => (
                  <li key={i}>{it}</li>
                ))}
              </Box>
            </Step>

            <Callout tone="star" text={t('userGuide.diners.callout')} />

            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" fontWeight={800} gutterBottom>
                {t('userGuide.diners.faqHeading')}
              </Typography>
              <Faq items={dinerFaq} />
            </Box>
          </Stack>
        )}
      </Container>

      <Box sx={{ textAlign: 'center', py: 3, bgcolor: '#f9f9f9', color: 'text.secondary', fontSize: 13 }}>
        {t('userGuide.footer')}
      </Box>
    </>
  );
}
